import { memo, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import type { Transition } from 'framer-motion';

import fallbackThumbnail from '../../../../../assets/thumbnails/game_thumbnail_seven.png';
import { SocialCoin } from '../../../../../components/SocialCoin/SocialCoin';
import { SpriteAnimation } from '../../../../../components/SpriteAnimation/SpriteAnimation';
import type { LoadedSprite } from '../../../../../components/SpriteAnimation/types';
import { SweepCoin } from '../../../../../components/SweepCoin/SweepCoin';
import { useFitText } from '../../../../../hooks/useFitText';
import { useFullyInView } from '../../../../../hooks/useFullyInView';
import { AMOUNT_MAX_WIDTH } from '../constants';
import type { WinnerTileVM } from '../types';

import styles from './WinnerAnimatedTile.module.scss';

// Every coin-burst sprite plays over this duration. The per-frame interval is derived
// from each sprite's own frame count so sprites with different frame counts (jackpot
// 40f, big win 35f) all take the same ~1.5s to finish.
const SPRITE_DURATION_MS = 1500;

// Shared swap transition. With AnimatePresence mode="wait" the title exit
// (0.4s) and the nickname/thumb entrance (0.4s) run back-to-back → 0.8s total.
const SWAP_TRANSITION: Transition = { duration: 0.4, ease: [0.33, 1, 0.68, 1] };

interface WinnerAnimatedTileProps {
  tile: WinnerTileVM;
  sprite: LoadedSprite;
  viewportRef: React.RefObject<HTMLDivElement>;
  titleSrc: string;
  titleAlt: string;
  variant?: 'jackpot' | 'bigWin';
  playBurst?: boolean;
  onSelect?: (gameId: number) => void;
}

export const WinnerAnimatedTile = memo(
  ({
    tile,
    sprite,
    viewportRef,
    titleSrc,
    titleAlt,
    variant = 'jackpot',
    playBurst = true,
    onSelect,
  }: WinnerAnimatedTileProps) => {
    const tileRef = useRef<HTMLDivElement>(null);
    const isSweep = tile.mode === 'SC';
    const fullyVisible = useFullyInView(tileRef, viewportRef);
    const amountText = tile.amount.toLocaleString();
    const fitAmountRef = useFitText(17, 7, AMOUNT_MAX_WIDTH, amountText);
    const variantClass = variant === 'bigWin' ? styles.tileBigWin : isSweep ? styles.tileSc : styles.tileGc;
    // Derived so any sprite (regardless of frame count) plays for SPRITE_DURATION_MS.
    const frameInterval = sprite.frameCount > 0 ? SPRITE_DURATION_MS / sprite.frameCount : 0;

    // Play the coin burst once — the first time the tile is fully on-screen. The settled
    // copy (playBurst === false) starts already 'done' and never plays the sprite.
    const [spritePhase, setSpritePhase] = useState<'idle' | 'playing' | 'done'>(playBurst ? 'idle' : 'done');
    useEffect(() => {
      if (playBurst && spritePhase === 'idle' && fullyVisible && sprite.ready) {
        setSpritePhase('playing');
      }
    }, [playBurst, spritePhase, fullyVisible, sprite.ready]);

    return (
      <div
        ref={tileRef}
        className={classNames(styles.tile, variantClass, {
          [styles.aligned]: spritePhase === 'done',
          [styles.notClickable]: !onSelect,
        })}
        onClick={onSelect ? () => onSelect(tile.gameId) : undefined}
      >
        {variant === 'bigWin' && spritePhase !== 'done' && <span className={styles.bigWinGlow} />}
        {spritePhase === 'playing' && (
          <SpriteAnimation
            isPlaying
            className={styles.sprite}
            frameInterval={frameInterval}
            initialSprite={sprite}
            sprite={sprite}
            onCycleComplete={() => setSpritePhase('done')}
          />
        )}
        {spritePhase === 'done' && (
          <motion.img
            alt="provider logo"
            animate={{ opacity: 1, x: 0 }}
            className={styles.thumb}
            draggable={false}
            initial={{ opacity: 0, x: -60 }}
            src={tile.gameImageUrl || fallbackThumbnail}
            transition={SWAP_TRANSITION}
          />
        )}
        <motion.div layout className={styles.info} transition={SWAP_TRANSITION}>
          <div className={styles.titleSlot}>
            <AnimatePresence initial={false} mode="wait">
              {spritePhase === 'done' ? (
                <motion.div
                  key="nickname"
                  animate={{ opacity: 1, y: 0 }}
                  className={styles.swapLayer}
                  initial={{ opacity: 0, y: -16 }}
                  transition={SWAP_TRANSITION}
                >
                  <span className={styles.nickname}>{tile.nickname}</span>
                </motion.div>
              ) : (
                <motion.div
                  key="title"
                  className={styles.swapLayer}
                  exit={{ opacity: 0, y: -60 }}
                  transition={SWAP_TRANSITION}
                >
                  <img alt={titleAlt} className={styles.jackpotTitle} draggable={false} src={titleSrc} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <span className={styles.amountRow}>
            {isSweep ? (
              <SweepCoin alt="sweep coin" className={styles.coin} />
            ) : (
              <SocialCoin alt="social coin" className={styles.coin} />
            )}
            <span ref={fitAmountRef} className={classNames(styles.amount, isSweep ? styles.amountSc : styles.amountCc)}>
              {amountText}
            </span>
          </span>
        </motion.div>
      </div>
    );
  },
);

WinnerAnimatedTile.displayName = 'WinnerAnimatedTile';
